import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { AG_GRID_LOCALE_RU, BlickAgGrid } from 'blick.react.aggrid';
import { Button, Col, Form, Modal, Row, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ConfirmDialogProps } from '../../utils/ConfirmDialog';
import { confirm } from '../../utils/confirm';
import { PlayFill, StopFill, ArrowRepeat, Download, Trash, ArrowClockwise, Save, XCircle } from 'react-bootstrap-icons';
import { centerClient, modelDeviceServiceClient } from '../../shared/clients';
import useReconnectingWebSocket from '../../utils/useReconnectingWebSocket';
import type { ModelDeviceServiceRequest, ModelDeviceServiceOperationResult, InstallModelDeviceServiceRequest, RemoveModelDeviceServiceRequest, UpdateModelDeviceServiceSettingsRequest, ModelDeviceServicesInfo, ModelInfo, AccessProtocol } from '../../shared/generated';
import { ModelDeviceServiceStatus, AccessProtocol as AccessProtocolEnum } from '../../shared/generated';
import { InputPicker } from 'rsuite';

interface IServiceTableRow {
    ModelId: number;
    ModelName: string;
    ServiceName: string;
    Status: ModelDeviceServiceStatus;
    StatusDescription?: string;
    GrpcPort: number;
    AccessPort: number;
    Protocol: AccessProtocol;
    EnableMetricServer: boolean;
    MetricsPort: number;
    // Результат фонового health-check (ModelDeviceServiceHealthWorker)
    HealthOk?: boolean;
    HealthReason?: string;
    LastError?: string;
    HealthStatusDescription?: string;
}

// Для agSelectCellEditor значения строковые (имена членов AccessProtocol);
// enum-значение <-> строка: AccessProtocolEnum[value] / AccessProtocolEnum[value as keyof...].
// "true"/"false" -> boolean при чтении
const SERVICE_PROTOCOLS: string[] = [AccessProtocolEnum[AccessProtocolEnum.UDP], AccessProtocolEnum[AccessProtocolEnum.TCP], AccessProtocolEnum[AccessProtocolEnum.Socket_UDP]];

const protocolToName = (protocol: AccessProtocol): string => AccessProtocolEnum[protocol];
const protocolFromName = (name: string): AccessProtocol => {
    const parsed = (AccessProtocolEnum as Record<string, AccessProtocol>)[name];
    return parsed ?? AccessProtocolEnum.None;
};

// Для agSelectCellEditor значения строковые; "true"/"false" -> boolean при чтении
const METRIC_TOGGLE_VALUES = ["true", "false"];

interface IServiceSettingsValues {
    GrpcPort: number;
    AccessPort: number;
    Protocol: AccessProtocol;
    EnableMetricServer: boolean;
    MetricsPort: number;
}

// WebSocket требует JWT (query string ?access_token=), т.к. браузерный WS API не умеет заголовки.
// Фабрика: URL пересобирается перед каждым подключением (актуальный токен).
const buildWsUrl = () => {
    const base = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
    const token = sessionStorage.getItem('access-token');
    return token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
};

const getStatusDescription = (status: ModelDeviceServiceStatus) => {
    switch (status) {
        case ModelDeviceServiceStatus.Running:
            return "Запущена";
        case ModelDeviceServiceStatus.Stopped:
            return "Остановлена";
        case ModelDeviceServiceStatus.StartPending:
            return "Запускается";
        case ModelDeviceServiceStatus.StopPending:
            return "Останавливается";
        case ModelDeviceServiceStatus.NotInstalled:
            return "Не установлена";
        case ModelDeviceServiceStatus.Unsupported:
            return "Не поддерживается (Linux)";
        default:
            return "Неизвестно";
    }
}

const ServiceManagement = () => {
    const [data, setData] = useState<IServiceTableRow[]>([]);
    const [managementSupported, setManagementSupported] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [busyRows, setBusyRows] = useState<Set<number>>(new Set());

    // Диалог установки
    const [installModel, setInstallModel] = useState<IServiceTableRow | null>(null);
    const [installGrpcPort, setInstallGrpcPort] = useState<number>(0);
    const [installAccessPort, setInstallAccessPort] = useState<number>(0);
    const [installProtocol, setInstallProtocol] = useState<AccessProtocol>(AccessProtocolEnum.Socket_UDP);
    const [installEnableMetrics, setInstallEnableMetrics] = useState<boolean>(true);
    const [installMetricsPort, setInstallMetricsPort] = useState<number>(7002);
    const [installStart, setInstallStart] = useState<boolean>(true);
    const [installForce, setInstallForce] = useState<boolean>(false);
    // Режим "новой установки": выбор модели, которой ещё нет в таблице (службы всех моделей
    // со статусом != NotInstalled скрыты фильтром на сервере - их ставят через этот диалог)
    const [showInstallPicker, setShowInstallPicker] = useState<boolean>(false);
    const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);

    const tableRef = useRef<AgGridReact<IServiceTableRow>>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const sizeToFit = useCallback(() => {
        // AG Grid #29: не вызываем sizeColumnsToFit, если контейнер ещё не виден (нулевая ширина, скрытый таб)
        const el = tableContainerRef.current;
        if (!el || el.clientWidth === 0)
            return;
        setTimeout(() => {
            tableRef.current?.api?.sizeColumnsToFit({
                defaultMinWidth: 50,
            });
        }, 0)
    }, []);

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el)
            return;

        // Ресайз колонок при первом показе таба и изменении размеров контейнера
        const observer = new ResizeObserver(() => {
            sizeToFit();
        });
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [sizeToFit]);

    const setRowBusy = (modelId: number, busy: boolean) => {
        setBusyRows(prev => {
            const next = new Set(prev);
            if (busy)
                next.add(modelId);
            else
                next.delete(modelId);
            return next;
        });
    };

    const loadServices = () => {
        modelDeviceServiceClient.getModelDeviceServices()
            .then((response) => {
                const info = response?.data;
                if (!info)
                    return;
                applyServicesInfo(info);
            })
            .catch(() => void 0)
            .finally(() => setIsLoading(false));
    }

    const updateStatuses = () => {
        modelDeviceServiceClient.getModelDeviceServices()
            .then((response) => {
                const info = response?.data;
                if (!info)
                    return;
                applyServicesInfo(info);
            })
            .catch(() => void 0);
    }

    // Применение данных о службах (из WebSocket или HTTP): ПОЛНАЯ синхронизация строк -
    // добавление новых моделей, удаление пропавших (напр. после Remove по WS) и обновление
    // значений. Порты/протокол/метрики берутся с сервера только для не-dirty строк (там
    // несохранённые значения пользователя).
    const applyServicesInfo = (info: ModelDeviceServicesInfo) => {
        setManagementSupported(info.ManagementSupported ?? false);
        const serverItems = (info.Items ?? []).map(item => ({
            ...item,
            StatusDescription: item.StatusDescription ?? getStatusDescription(item.Status)
        }));
        const serverByModelId = new Map(serverItems.map(item => [item.ModelId, item]));

        // Удалённые на сервере модели: вычистить dirty/saved-состояние
        const removedIds: number[] = [];
        data.forEach(row => {
            if (!serverByModelId.has(row.ModelId))
                removedIds.push(row.ModelId);
        });
        if (removedIds.length > 0) {
            setDirtySettings(prev => {
                const next = new Map(prev);
                for (const id of removedIds)
                    next.delete(id);
                return next;
            });
            setSavedSettings(prev => {
                const next = new Map(prev);
                for (const id of removedIds)
                    next.delete(id);
                return next;
            });
        }

        // dirty-строки: базлайн (savedSettings) не трогаем, значения ячеек остаются пользовательскими
        const dirty = dirtySettingsRef.current;
        const nextRows = serverItems.map(item => {
            if (dirty.has(item.ModelId))
                return item;
            // не-dirty: серверные значения - одновременно и новое состояние, и новый базлайн
            setSavedSettings(prev => new Map(prev).set(item.ModelId, {
                GrpcPort: item.GrpcPort,
                AccessPort: item.AccessPort,
                Protocol: item.Protocol,
                EnableMetricServer: item.EnableMetricServer,
                MetricsPort: item.MetricsPort
            }));
            return item;
        });
        setData(nextRows);
        // Перерисовка подсветки dirty-ячеек после синхронизации строк
        setTimeout(() => {
            tableRef.current?.api.refreshCells({ force: true });
        }, 0);
    }

    useEffect(() => {
        setIsLoading(true);
        loadServices();
    }, []);

    // WebSocket вместо поллинга: статусы приходят событиями (после операций и
    // при фоновом мониторинге внешних изменений раз в 3с на сервере).
    // Авто-reconnect с экспоненциальной задержкой.
    useReconnectingWebSocket(buildWsUrl, {
        onMessage: (m) => {
            let payload: unknown = m;
            if (payload && typeof payload === 'object' && 'data' in payload)
                payload = (payload as { data: unknown }).data;
            if (typeof payload === 'string') {
                try {
                    payload = JSON.parse(payload) as unknown;
                } catch {
                    return;
                }
            }
            const message = payload as { Type?: string; Data?: ModelDeviceServicesInfo } | null | undefined;
            if (message?.Type !== "ModelDeviceServices")
                return;
            const info = message.Data;
            if (!info)
                return;
            setManagementSupported(info.ManagementSupported ?? false);
            applyServicesInfo(info);
        },
    });

    const performOperation = (url: string, row: IServiceTableRow, successMessage: string, body?: object) => {
        setRowBusy(row.ModelId, true);
        const request = body ?? { ModelId: row.ModelId, ModelName: row.ModelName };
        let operation: Promise<{ data?: ModelDeviceServiceOperationResult }>;
        switch (url) {
            case "StartModelDeviceService":
                operation = modelDeviceServiceClient.startModelDeviceService(request as ModelDeviceServiceRequest);
                break;
            case "StopModelDeviceService":
                operation = modelDeviceServiceClient.stopModelDeviceService(request as ModelDeviceServiceRequest);
                break;
            case "RestartModelDeviceService":
                operation = modelDeviceServiceClient.restartModelDeviceService(request as ModelDeviceServiceRequest);
                break;
            case "RemoveModelDeviceService":
                operation = modelDeviceServiceClient.removeModelDeviceService(request as RemoveModelDeviceServiceRequest);
                break;
            case "InstallModelDeviceService":
                operation = modelDeviceServiceClient.installModelDeviceService(request as InstallModelDeviceServiceRequest);
                break;
            default:
                operation = Promise.reject(new Error(`Неизвестная операция: ${url}`));
        }
        operation
            .then((response) => {
                const result = response?.data;
                if (result?.Success)
                    toast.success(result.Message || successMessage);
                else
                    toast.error(formatOperationError(result, "Операция не выполнена."));
                updateStatuses();
            })
            .catch(() => void 0)
            .finally(() => setRowBusy(row.ModelId, false));
    }

    const startService = (row: IServiceTableRow) => {
        performOperation("StartModelDeviceService", row, `Служба ${row.ServiceName} запущена.`);
    }

    const stopService = async (row: IServiceTableRow) => {
        const stopConfirm = confirm(new ConfirmDialogProps(`Остановить службу ${row.ServiceName}?`));
        if (await stopConfirm)
            performOperation("StopModelDeviceService", row, `Служба ${row.ServiceName} остановлена.`);
    }

    const restartService = async (row: IServiceTableRow) => {
        const restartConfirm = confirm(new ConfirmDialogProps(`Перезапустить службу ${row.ServiceName}?`));
        if (await restartConfirm)
            performOperation("RestartModelDeviceService", row, `Служба ${row.ServiceName} перезапущена.`);
    }

    const removeService = async (row: IServiceTableRow) => {
        // Шаг 1: подтверждение удаления. confirm резолвится false при отмене -
        // любое falsy-значение отменяет операцию (иначе "Отмена" удаляла бы службу).
        const removeConfirm = await confirm(new ConfirmDialogProps(`Удалить службу ${row.ServiceName}?`));
        if (!removeConfirm)
            return;
        // Шаг 2: вопрос про файлы экземпляра (Firmware/Logs/States)
        const deleteFiles = await confirm(new ConfirmDialogProps(
            `Также удалить файлы экземпляра (Firmware/Logs/States)?`));
        performOperation("RemoveModelDeviceService", row, `Служба ${row.ServiceName} удалена.`,
            { ModelId: row.ModelId, ModelName: row.ModelName, DeleteFiles: deleteFiles === true });
    }

    // Единый формат ошибки: Message + Error (stderr скрипта) + ScriptOutput
    const formatOperationError = (result?: ModelDeviceServiceOperationResult, fallback?: string) => {
        const parts = [result?.Message || fallback];
        if (result?.Error)
            parts.push(result.Error);
        else if (result?.ScriptOutput)
            parts.push(result.ScriptOutput);
        return parts.filter(p => p).join("\n");
    }

    const openInstallDialog = (row: IServiceTableRow) => {
        setInstallGrpcPort(row.GrpcPort || 5003);
        setInstallAccessPort(row.AccessPort || 40400);
        setInstallProtocol(row.Protocol || AccessProtocolEnum.Socket_UDP);
        setInstallEnableMetrics(row.EnableMetricServer ?? true);
        setInstallMetricsPort(row.MetricsPort || 7002);
        setInstallStart(true);
        setInstallForce(row.Status != ModelDeviceServiceStatus.NotInstalled);
        setInstallModel(row);
    }

    // Установка службы для модели, которой ещё нет в таблице: список моделей - из
    // GetDeviceModels (ModelBox) минус уже отображаемые; выбор создаёт синтетическую строку
    const openInstallPicker = () => {
        const existing = new Set(data.map(row => row.ModelId));
        centerClient.getDeviceModels()
            .then((response) => {
                const models = (response?.data?.Items ?? [])
                    .filter((m): m is ModelInfo => !!m && m.ModelId != null && !!m.ModelName)
                    .filter(m => !existing.has(m.ModelId));
                if (models.length === 0) {
                    toast.info("Все известные модели уже отображаются в таблице.");
                    return;
                }
                setAvailableModels(models);
                setShowInstallPicker(true);
            })
            .catch(() => toast.error("Не удалось загрузить список моделей."));
    }

    // Выбор модели в пикере: подставить её в install-модал с дефолтными портами
    const pickModelForInstall = (model: ModelInfo | null) => {
        if (!model)
            return;
        setInstallGrpcPort(5003);
        setInstallAccessPort(40400);
        setInstallProtocol(AccessProtocolEnum.Socket_UDP);
        setInstallEnableMetrics(true);
        setInstallMetricsPort(7002);
        setInstallStart(true);
        setInstallForce(false);
        setInstallModel({
            ModelId: model.ModelId,
            ModelName: model.ModelName ?? "",
            // Точное имя службы знает только сервер (ModelDeviceService_{Model.ToString()}),
            // здесь - оценочное из Alias для заголовка модала; на службу не влияет
            ServiceName: `ModelDeviceService_${model.ModelName}`,
            Status: ModelDeviceServiceStatus.NotInstalled,
            GrpcPort: 5003,
            AccessPort: 40400,
            Protocol: AccessProtocolEnum.Socket_UDP,
            EnableMetricServer: true,
            MetricsPort: 7002,
        });
    }

    // Единое закрытие install-модала: сброс и выбранной модели, и режима пикера
    const closeInstallDialog = () => {
        setInstallModel(null);
        setShowInstallPicker(false);
    }

    const performInstall = () => {
        if (!installModel)
            return;
        const request: InstallModelDeviceServiceRequest = {
            ModelId: installModel.ModelId,
            ModelName: installModel.ModelName,
            GrpcPort: installGrpcPort,
            AccessPort: installAccessPort,
            Protocol: installProtocol,
            EnableMetricServer: installEnableMetrics,
            MetricsPort: installMetricsPort,
            Start: installStart,
            ForceReinstall: installForce,
        };
        const row = installModel;
        closeInstallDialog();
        setRowBusy(row.ModelId, true);
        modelDeviceServiceClient.installModelDeviceService(request)
            .then((response) => {
                const result = response?.data;
                if (result?.Success)
                    toast.success(result.Message || "Служба установлена.");
                else
                    toast.error(formatOperationError(result, "Установка не выполнена."));
                updateStatuses();
            })
            .catch(() => void 0)
            .finally(() => setRowBusy(row.ModelId, false));
    }

    // --- Настройки службы (порты/протокол/метрики): dirty-трекинг + сохранение ---

    // modelId -> исходные (последние загруженные/сохранённые) значения
    const [savedSettings, setSavedSettings] = useState<Map<number, IServiceSettingsValues>>(new Map());
    // modelId -> изменённые значения
    const [dirtySettings, setDirtySettings] = useState<Map<number, IServiceSettingsValues>>(new Map());
    // Ref-зеркало dirtySettings для WS-обработчика (актуальные значения без устаревшего замыкания)
    const dirtySettingsRef = useRef(dirtySettings);
    dirtySettingsRef.current = dirtySettings;

    const isRowDirty = (modelId: number) => dirtySettings.has(modelId);

    const getCellClass = (field: keyof IServiceSettingsValues) =>
        (params: { data?: IServiceTableRow }) => {
            const item = params.data;
            if (!item)
                return "";
            const saved = savedSettings.get(item.ModelId);
            const current = item[field];
            const original = saved ? saved[field] : current;
            return current !== original ? "service-cell-dirty" : "";
        };

    const onCellValueChanged = (params: CellValueChangedEvent<IServiceTableRow>) => {
        const item = params.data;
        const field = params.colDef?.field;
        if (!item || !field || !["GrpcPort", "AccessPort", "Protocol", "EnableMetricServer", "MetricsPort"].includes(field))
            return;
        const saved = savedSettings.get(item.ModelId) ?? {
            GrpcPort: item.GrpcPort,
            AccessPort: item.AccessPort,
            Protocol: item.Protocol,
            EnableMetricServer: item.EnableMetricServer,
            MetricsPort: item.MetricsPort
        };
        const dirty: IServiceSettingsValues = {
            GrpcPort: item.GrpcPort,
            AccessPort: item.AccessPort,
            // agSelectCellEditor пишет строку (имя члена enum) → обратно в AccessProtocol;
            // без правки значение приходит уже enum'ом
            Protocol: typeof item.Protocol === "string" ? protocolFromName(item.Protocol as unknown as string) : item.Protocol,
            // agSelectCellEditor пишет строки; приводим к boolean
            EnableMetricServer: (item.EnableMetricServer as unknown) === "true" || item.EnableMetricServer === true,
            MetricsPort: item.MetricsPort
        };
        const unchanged =
            dirty.GrpcPort === saved.GrpcPort &&
            dirty.Protocol === saved.Protocol &&
            dirty.EnableMetricServer === saved.EnableMetricServer &&
            dirty.MetricsPort === saved.MetricsPort;
        setDirtySettings(prev => {
            const next = new Map(prev);
            if (unchanged)
                next.delete(item.ModelId);
            else
                next.set(item.ModelId, dirty);
            return next;
        });
        agGridRefreshCells();
    };

    // Перерисовка подсветки dirty-ячеек после изменений
    const agGridRefreshCells = () => {
        setTimeout(() => {
            tableRef.current?.api.refreshCells({ force: true });
        }, 0);
    }

    const cancelRowSettings = (row: IServiceTableRow) => {
        const saved = savedSettings.get(row.ModelId);
        if (!saved)
            return;
        setDirtySettings(prev => {
            const next = new Map(prev);
            next.delete(row.ModelId);
            return next;
        });
        tableRef.current?.api.forEachNode((node) => {
            if (node.data?.ModelId !== row.ModelId)
                return;
            node.setDataValue('GrpcPort', saved.GrpcPort);
            node.setDataValue('AccessPort', saved.AccessPort);
            node.setDataValue('Protocol', protocolToName(saved.Protocol));
            node.setDataValue('EnableMetricServer', saved.EnableMetricServer);
            node.setDataValue('MetricsPort', saved.MetricsPort);
        });
        agGridRefreshCells();
    }

    const saveRowSettings = (row: IServiceTableRow) => {
        const dirty = dirtySettings.get(row.ModelId);
        if (!dirty)
            return;
        const request: UpdateModelDeviceServiceSettingsRequest = {
            ModelId: row.ModelId,
            ModelName: row.ModelName,
            GrpcPort: dirty.GrpcPort,
            AccessPort: dirty.AccessPort,
            Protocol: dirty.Protocol,
            EnableMetricServer: dirty.EnableMetricServer,
            MetricsPort: dirty.MetricsPort,
        };
        setRowBusy(row.ModelId, true);
        modelDeviceServiceClient.updateModelDeviceServiceSettings(request)
            .then((response) => {
                const result = response?.data;
                if (result?.Success) {
                    toast.success(result.Message || "Настройки сохранены.");
                    setSavedSettings(prev => new Map(prev).set(row.ModelId, { ...dirty }));
                    setDirtySettings(prev => {
                        const next = new Map(prev);
                        next.delete(row.ModelId);
                        return next;
                    });
                } else {
                    toast.error(formatOperationError(result, "Не удалось сохранить настройки."));
                }
                updateStatuses();
            })
            .catch(() => void 0)
            .finally(() => setRowBusy(row.ModelId, false));
    }

    const columns = useMemo<ColDef<IServiceTableRow>[]>(() => [
        {
            headerName: "№",
            maxWidth: 60,
            editable: false,
            sortable: false,
            filter: false,
            cellRenderer: (row: CustomCellRendererProps<IServiceTableRow>) => {
                const item = row.data;
                if (!item)
                    return <></>;
                const status = item.Status;
                let bg = 'var(--bg-lighter)';
                if (status == ModelDeviceServiceStatus.Running) bg = 'var(--more-hover-bg)';
                else if (status == ModelDeviceServiceStatus.Stopped) bg = 'var(--banned)';
                else if (status == ModelDeviceServiceStatus.NotInstalled) bg = 'var(--blocked)';
                const isDirty = isRowDirty(item.ModelId);
                return (
                    <Badge pill style={{ backgroundColor: bg, color: 'var(--text-dark)' }}>
                        {row.node?.rowIndex != null ? row.node.rowIndex + 1 : ''}{isDirty ? ' *' : ''}
                    </Badge>
                );
            }
        },
        {
            field: 'ModelName',
            headerName: "Модель",
            editable: false,
        },
        {
            field: 'ModelId',
            headerName: "ModelId",
            editable: false,
            maxWidth: 90,
        },
        {
            field: 'ServiceName',
            headerName: "Служба",
            editable: false,
        },
        {
            field: 'Status',
            headerName: "Статус",
            editable: false,
            cellRenderer: (row: CustomCellRendererProps<IServiceTableRow>) => getStatusDescription(row.value),
            cellClassRules: {
                'service-status-running': (params) => params.value == ModelDeviceServiceStatus.Running,
                'service-status-stopped': (params) => params.value == ModelDeviceServiceStatus.Stopped,
                'service-status-notinstalled': (params) => params.value == ModelDeviceServiceStatus.NotInstalled,
            }
        },
        {
            field: 'HealthOk',
            headerName: "Отзывчивость",
            editable: false,
            maxWidth: 140,
            valueFormatter: (p) => p.value === true ? "Отвечает" : p.value === false ? "Нет ответа" : "—",
            cellRenderer: (row: CustomCellRendererProps<IServiceTableRow>) => {
                const item = row.data;
                if (!item || item.HealthOk == null)
                    return <span title="Проверка не проводилась">—</span>;
                const title = [
                    item.HealthReason,
                    item.LastError ? `Последняя ошибка MDS: ${item.LastError}` : "",
                ].filter(Boolean).join("\n");
                return (
                    <span className={item.HealthOk ? 'service-health-ok' : 'service-health-fail'} title={title || undefined}>
                        {item.HealthOk ? "Отвечает" : "Нет ответа"}
                    </span>
                );
            }
        },
        {
            field: 'GrpcPort',
            headerName: "gRPC порт",
            editable: true,
            maxWidth: 110,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: { min: 1, max: 65535, precision: 0 },
            cellClass: getCellClass('GrpcPort'),
        },
        {
            field: 'AccessPort',
            headerName: "AccessPort",
            editable: true,
            maxWidth: 130,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: { min: 1, max: 65535, precision: 0 },
            cellClass: getCellClass('AccessPort'),
        },
        {
            field: 'Protocol',
            headerName: "Тип соединения",
            editable: true,
            minWidth: 150,
            maxWidth: 180,
            // В ячейке enum: отображение — имя члена ("Socket_UDP"), редактор — agSelect со строками
            valueFormatter: (p) => (p.value == null ? "" : protocolToName(p.value as AccessProtocol)),
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: SERVICE_PROTOCOLS },
            cellClass: getCellClass('Protocol'),
        },
        {
            field: 'EnableMetricServer',
            headerName: "Метрики",
            editable: true,
            minWidth: 110,
            maxWidth: 130,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: METRIC_TOGGLE_VALUES },
            valueFormatter: (params) => params.value === true || params.value === "true" ? "Вкл" : "Выкл",
            cellClass: getCellClass('EnableMetricServer'),
        },
        {
            field: 'MetricsPort',
            headerName: "Metric порт",
            editable: true,
            maxWidth: 120,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: { min: 1, max: 65535, precision: 0 },
            cellClass: getCellClass('MetricsPort'),
        },
        {
            headerName: "Действия",
            editable: false,
            sortable: false,
            filter: false,
            minWidth: 400,
            cellRenderer: (row: CustomCellRendererProps<IServiceTableRow>) => {
                const item = row.data;
                if (!item)
                    return <></>;
                const isBusy = busyRows.has(item.ModelId);
                const unsupported = item.Status == ModelDeviceServiceStatus.Unsupported;
                const canStart = !unsupported && (item.Status == ModelDeviceServiceStatus.Stopped || item.Status == ModelDeviceServiceStatus.NotInstalled);
                const canStop = !unsupported && item.Status == ModelDeviceServiceStatus.Running;
                const isDirty = isRowDirty(item.ModelId);
                if (isDirty) {
                    return (
                        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', height: '100%' }}>
                            <Button size='sm' variant="custom" title="Сохранить настройки" disabled={isBusy}
                                onClick={() => saveRowSettings(item)}><Save /></Button>
                            <Button size='sm' variant="custom" title="Отменить изменения" disabled={isBusy}
                                onClick={() => cancelRowSettings(item)}><XCircle /></Button>
                        </div>
                    );
                }
                return (
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', height: '100%' }}>
                        <Button size='sm' variant="custom" title="Запустить" disabled={!canStart || isBusy}
                            onClick={() => startService(item)}><PlayFill /></Button>
                        <Button size='sm' variant="custom" title="Остановить" disabled={!canStop || isBusy}
                            onClick={() => stopService(item)}><StopFill /></Button>
                        <Button size='sm' variant="custom" title="Перезапустить" disabled={!canStop || isBusy}
                            onClick={() => restartService(item)}><ArrowRepeat /></Button>
                        <Button size='sm' variant="custom" title="Установить" disabled={unsupported || isBusy}
                            onClick={() => openInstallDialog(item)}><Download /></Button>
                        <Button size='sm' variant="custom" title="Удалить"
                            disabled={unsupported || item.Status == ModelDeviceServiceStatus.NotInstalled || isBusy}
                            onClick={() => removeService(item)}><Trash /></Button>
                    </div>
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- обработчики замыкают только стабильные setState-функции
    ], [busyRows, dirtySettings, savedSettings]);

    if (!managementSupported && data.length > 0 && data.every(item => item.Status == ModelDeviceServiceStatus.Unsupported)) {
        return (
            <div style={{ padding: '2rem' }}>
                <h5>Управление службами ModelDeviceService на Linux пока не реализовано.</h5>
            </div>
        );
    }

    return (
        <div style={{ height: '100%' }}>
            <Modal show={installModel != null || showInstallPicker} onHide={closeInstallDialog}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Установка службы {installModel?.ServiceName ?? ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {showInstallPicker && (
                            <Row className="marginTop">
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Модель</Form.Label>
                                        <InputPicker
                                            block
                                            cleanable={false}
                                            placeholder="Выберите модель..."
                                            data={availableModels.map(m => ({ label: m.ModelName ?? "", value: m.ModelId }))}
                                            value={installModel?.ModelId ?? null}
                                            onChange={(value: number | null) => {
                                                const model = availableModels.find(m => m.ModelId === value) ?? null;
                                                pickModelForInstall(model);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col>
                                <Form.Group>
                                    <Form.Label>gRPC порт</Form.Label>
                                    <Form.Control type="number" value={installGrpcPort}
                                        onChange={(e) => setInstallGrpcPort(parseInt(e.target.value) || 0)} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label>AccessPort (UDP, устройства)</Form.Label>
                                    <Form.Control type="number" value={installAccessPort}
                                        onChange={(e) => setInstallAccessPort(parseInt(e.target.value) || 0)} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="marginTop">
                            <Col>
                                <Form.Group>
                                    <Form.Label>Тип соединения</Form.Label>
                                    <InputPicker
                                        block
                                        cleanable={false}
                                        data={SERVICE_PROTOCOLS.map(p => ({ label: p, value: p }))}
                                        value={protocolToName(installProtocol)}
                                        onChange={(value: string | null) => setInstallProtocol(protocolFromName(value ?? "Socket_UDP"))}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="marginTop">
                            <Col>
                                <Form.Check id="installEnableMetrics" type="checkbox" label="Запускать сервер метрик (Prometheus)"
                                    checked={installEnableMetrics}
                                    onChange={(e) => setInstallEnableMetrics(e.target.checked)} />
                            </Col>
                            <Col>
                                {installEnableMetrics && (
                                    <Form.Group>
                                        <Form.Label>Metric порт</Form.Label>
                                        <Form.Control type="number" value={installMetricsPort}
                                            onChange={(e) => setInstallMetricsPort(parseInt(e.target.value) || 0)} />
                                    </Form.Group>
                                )}
                            </Col>
                        </Row>
                        <Row className="marginTop">
                            <Col>
                                <Form.Check id="forInstallStart" type="checkbox" label="Запустить после установки"
                                    checked={installStart} onChange={(e) => setInstallStart(e.target.checked)} />
                            </Col>
                            <Col>
                                <Form.Check id="forInstallForce" type="checkbox" label="Переустановить существующую службу"
                                    checked={installForce} onChange={(e) => setInstallForce(e.target.checked)} />
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeInstallDialog}>Отмена</Button>
                    <Button variant="custom" onClick={performInstall} disabled={!installModel}>Установить</Button>
                </Modal.Footer>
            </Modal>
            <div style={{ width: '100%', padding: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button variant="custom" onClick={() => { setIsLoading(true); loadServices(); }}>
                        <ArrowClockwise /> Обновить
                    </Button>
                    {managementSupported && (
                        <Button variant="custom" onClick={openInstallPicker}>
                            <Download /> Установить службу
                        </Button>
                    )}
                </div>
                <div>
                    {managementSupported ?
                        <label>Службы ModelDeviceService для каждой модели устройств. Порты, тип соединения и метрики редактируются в таблице; сохранение - кнопками строки (изменения отмечены *).</label>
                        : <label>Управление службами на Linux пока не реализовано</label>}
                </div>
            </div>
            <div ref={tableContainerRef} style={{ height: 'calc(100% - 4.5rem)' }}>
                <BlickAgGrid
                    showRestoreColumnsButton={true}
                    loading={isLoading}
                    rowData={data}
                    ref={tableRef}
                    localeText={AG_GRID_LOCALE_RU}
                    columnDefs={columns}
                    onCellValueChanged={onCellValueChanged}
                    onModelUpdated={(e) => e.api.refreshCells()}
                />
            </div>
        </div>
    );
};

export default ServiceManagement;